import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import type { FactProposal } from "@/types/chat.type";
import { brandUniversePendingFactProposalsKey } from "./useBrandUniversePendingFactProposals";

export const brandUniverseChatProposalsKey = (
  brandId: string,
  sessionId: string | null,
) => ["brandUniverseChatProposals", brandId, sessionId] as const;

/**
 * Session-scoped fact proposals (the pending tray + handled history). Switching
 * sessions changes the query key, so the tray/history auto-refetch. Disabled for
 * a draft session (no sessionId yet).
 */
export function useBrandUniverseChatProposals(
  brandId: string,
  sessionId: string | null,
) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: brandUniverseChatProposalsKey(brandId, sessionId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ proposals: FactProposal[] }>(
        `/brand-agent/${brandId}/sessions/${sessionId}/fact-proposals`,
      );
      return res.data.proposals;
    },
    enabled: !!sessionId,
  });

  const proposals = useMemo(() => query.data ?? [], [query.data]);
  const pending = useMemo(
    () => proposals.filter((p) => p.status === "pending"),
    [proposals],
  );
  const handled = useMemo(
    () => proposals.filter((p) => p.status !== "pending"),
    [proposals],
  );

  const invalidateProposals = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: brandUniverseChatProposalsKey(brandId, sessionId),
    });
    void queryClient.invalidateQueries({
      queryKey: brandUniversePendingFactProposalsKey(brandId),
    });
  }, [queryClient, brandId, sessionId]);

  /**
   * Invalidate a specific session's proposals — used by the chat stream, which
   * may create a brand-new session whose id differs from the one currently bound
   * to this hook (the draft `null`). Also refreshes the brand-global pending list.
   */
  const invalidateForSession = useCallback(
    (sid: string) => {
      void queryClient.invalidateQueries({
        queryKey: brandUniverseChatProposalsKey(brandId, sid),
      });
      void queryClient.invalidateQueries({
        queryKey: brandUniversePendingFactProposalsKey(brandId),
      });
    },
    [queryClient, brandId],
  );

  return {
    proposals,
    pending,
    handled,
    isLoading: query.isLoading,
    invalidateProposals,
    invalidateForSession,
  };
}
